import { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import {
  DateInputWithConfirm,
  InputWithConfirm,
  RadioOptionsWithConfirm,
  SelectWithConfirm,
  AutocompleteInputWithConfirm,
  IQuestion,
  getStringFromDate,
  getDateFromString,
  isValidDate,
} from 'shared-components';
import { countries } from 'platform-data';
import { ApplicationState } from '../../../../../store';
import { saveVisaPrepForm } from '../../../../../store/student/actions';
import { createUpdateVPIFieldAction } from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';

import { hasSpouseDetails, isPersonTravellingWith } from '../../../../../utilities/pages';
import { MaritalStatus, IDate } from '../../../../../types';
import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus } from './utils';
import { useNavigate } from 'react-router-dom';

const VisaPrepFormFamilyPage1 = (_props: any) => {
  // dispatch actions
  const dispatch = useDispatch();

  const onNext = () => {
    dispatch(saveVisaPrepForm(Pages.PAGE3_1));
  };

  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(createUpdateVPIFieldAction(fieldName, value, refValue));

  const navigate = useNavigate();
  const onBackToTaskList = () => navigate(-1);

  // state props from store
  const studentVisaPrepForm = useSelector(({ student }: ApplicationState) => student.visa, shallowEqual);

  // constants
  const currentStep = 1;
  const steps = 5;
  const nationalitiesNames = countries.map(nationality => nationality.nationality);

  const {
    maritalStatus,
    spouseNationality,
    spouseSameNationality,
    spouseFamilyName,
    spouseGivenName,
    spouseLivingWith,
    spouseTravellingWith,
    spouseUkVisa,
    spouseHasPassport,
    spousePassportNumber,
    spouseDob,
  } = studentVisaPrepForm;

  // component state
  const [complete, setComplete] = useState(false);
  const [dob, setDob] = useState<IDate>(() => getDateFromString(spouseDob));
  const [dobError, setDobError] = useState('');
  const [nationalitySuggestions, setNationalitySuggestions] = useState(nationalitiesNames);

  useEffect(() => {
    const { complete: newComplete } = getPageStatus({
      maritalStatus,
      spouseNationality,
      spouseSameNationality,
      spouseFamilyName,
      spouseGivenName,
      spouseLivingWith,
      spouseTravellingWith,
      spouseUkVisa,
      spouseHasPassport,
      spousePassportNumber,
      spouseDob,
      dobError,
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [
    complete,
    maritalStatus,
    spouseNationality,
    spouseSameNationality,
    spouseFamilyName,
    spouseGivenName,
    spouseLivingWith,
    spouseTravellingWith,
    spouseUkVisa,
    spouseHasPassport,
    spousePassportNumber,
    spouseDob,
    dobError,
  ]);

  const onNationalityChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = nationalitiesNames.filter(s => s.toLowerCase().includes(value.toLowerCase()));
    setNationalitySuggestions(suggestions);
    onChange(VQ.SPOUSE_NATIONALITY, value);
  };

  const onBlurNationality = (evt: any) => {
    const { value } = evt.currentTarget;
    if (nationalitiesNames.includes(value)) {
      onChange(VQ.SPOUSE_NATIONALITY, value);
    } else {
      onChange(VQ.SPOUSE_NATIONALITY, '');
    }
    setNationalitySuggestions(nationalitiesNames);
  };

  const validateDate = (dateValue: IDate) => {
    const { YYYY, DD, MM } = dateValue;
    if (YYYY && DD && MM) {
      const isValid = isValidDate(dateValue);
      if (!isValid) {
        setDobError(`Doesn't look like a valid date`);
      } else {
        onChange(VQ.SPOUSE_DOB, getStringFromDate(dateValue));
        setDobError('');
      }
    }
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onNext={onNext}
      onBackToTaskList={onBackToTaskList}
      section={'Family'}
    >
      <SelectWithConfirm
        key={VQ.MARITAL_STATUS}
        id={VQ.MARITAL_STATUS}
        label="What is your marital status?"
        options={[
          { label: 'Single', value: MaritalStatus.SINGLE },
          {
            label: 'Married or a civil partner',
            value: MaritalStatus.MARRIED,
          },
          { label: 'Unmarried partner', value: MaritalStatus.UNMARRIED },
          {
            label: 'Divorced or civil partnership dissolved',
            value: MaritalStatus.DIVORCED,
          },
          { label: 'Separated', value: MaritalStatus.SEPARATED },
          {
            label: 'Widowed or a surviving civil partner',
            value: MaritalStatus.WIDOWED,
          },
        ]}
        value={maritalStatus}
        onChange={(evt: any) => onChange(VQ.MARITAL_STATUS, evt.currentTarget.value)}
      />

      {maritalStatus !== undefined && hasSpouseDetails(maritalStatus) ? (
        <>
          <AutocompleteInputWithConfirm
            key={VQ.NATIONALITY}
            id={VQ.NATIONALITY}
            value={spouseNationality}
            label={'Spouse: Nationality'}
            onChange={onNationalityChange}
            suggestions={nationalitySuggestions}
            onBlur={onBlurNationality}
          />

          <RadioOptionsWithConfirm
            key={VQ.SAME_NATIONALITY}
            id={VQ.SAME_NATIONALITY}
            label={'Spouse: Have they always had the same nationality?'}
            onClick={(question: IQuestion) => onChange(VQ.SPOUSE_SAME_NATIONALITY, question.label === 'Yes')}
            isInline={true}
            questionMaxWidth={220}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: spouseSameNationality === true,
              },
              {
                char: 'N',
                label: 'No',
                selected: spouseSameNationality === false,
              },
            ]}
          />

          <InputWithConfirm
            key={VQ.FAMILY_NAME}
            id={VQ.FAMILY_NAME}
            value={spouseFamilyName}
            label={'Spouse: Family Name'}
            onChange={(evt: any) => onChange(VQ.SPOUSE_FAMILY_NAME, evt.currentTarget.value)}
          />
          <InputWithConfirm
            key={`${VQ.GIVEN_NAME}`}
            id={`${VQ.GIVEN_NAME}`}
            value={spouseGivenName}
            label={'Spouse: Given Name'}
            onChange={(evt: any) => onChange(VQ.SPOUSE_GIVEN_NAME, evt.currentTarget.value)}
          />

          <DateInputWithConfirm
            key={VQ.DOB}
            id={VQ.DOB}
            label={'Spouse: Date of Birth'}
            value={dob}
            errorText={dobError}
            onChangeDD={(evt: any) => {
              const spouseDob = {
                ...dob,
                DD: evt.currentTarget.value,
              };
              setDob(spouseDob);
            }}
            onChangeMM={(evt: any) => {
              const spouseDob = {
                ...dob,
                MM: evt.currentTarget.value,
              };
              setDob(spouseDob);
            }}
            onChangeYY={(evt: any) => {
              const { value } = evt.currentTarget;
              if (value.length === 4) {
                const spouseDob = {
                  ...dob,
                  YYYY: value,
                };
                setDob(spouseDob);

                validateDate({
                  ...dob,
                  YYYY: value,
                });
              } else {
                const spouseDob = {
                  ...dob,
                  YYYY: value,
                };
                setDob(spouseDob);
              }
            }}
            onBlur={(_evt: any) =>
              validateDate({
                ...dob,
              })
            }
          />

          <RadioOptionsWithConfirm
            key={VQ.LIVING_WITH}
            id={VQ.LIVING_WITH}
            label={'Does your spouse / partner currently live with you?'}
            onClick={(question: IQuestion) => onChange(VQ.SPOUSE_LIVING_WITH, question.label === 'Yes')}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: spouseLivingWith === true,
              },
              {
                char: 'N',
                label: 'No',
                selected: spouseLivingWith === false,
              },
            ]}
          />

          <RadioOptionsWithConfirm
            key={VQ.TRAVELLING_WITH}
            id={VQ.TRAVELLING_WITH}
            label={'Spouse: Is this person travelling with you?'}
            onClick={(question: IQuestion) => onChange(VQ.SPOUSE_TRAVELLING_WITH, question.label)}
            questions={[
              {
                char: '1',
                label: 'Yes - under 1 month',
                selected: spouseTravellingWith === 'Yes - under 1 month',
              },
              {
                char: '2',
                label: 'Yes - over 1 month',
                selected: spouseTravellingWith === 'Yes - over 1 month',
              },
              {
                char: '3',
                label: 'No - not travelling',
                selected: spouseTravellingWith === 'No - not travelling',
              },
            ]}
          />

          {isPersonTravellingWith(spouseTravellingWith) ? (
            <>
              <RadioOptionsWithConfirm
                key={VQ.HAS_UK_VISA}
                id={VQ.HAS_UK_VISA}
                label={'Spouse: Do they have a visa for the UK?'}
                onClick={(question: IQuestion) => onChange(VQ.SPOUSE_UK_VISA, question.label === 'Yes')}
                isInline={true}
                questions={[
                  {
                    char: 'Y',
                    label: 'Yes',
                    selected: spouseUkVisa === true,
                  },
                  {
                    char: 'N',
                    label: 'No',
                    selected: spouseUkVisa === false,
                  },
                ]}
              />
              <RadioOptionsWithConfirm
                key={VQ.HAS_PASSPORT}
                id={VQ.HAS_PASSPORT}
                label={'Spouse: Do they have a Passport or Travel Document?'}
                onClick={(question: IQuestion) => onChange(VQ.SPOUSE_HAS_PASSPORT, question.label === 'Yes')}
                isInline={true}
                questions={[
                  {
                    char: 'Y',
                    label: 'Yes',
                    selected: spouseHasPassport === true,
                  },
                  {
                    char: 'N',
                    label: 'No',
                    selected: spouseHasPassport === false,
                  },
                ]}
              />

              {spouseHasPassport ? (
                <InputWithConfirm
                  key={VQ.PASSPORT_NUMBER}
                  id={VQ.PASSPORT_NUMBER}
                  value={spousePassportNumber}
                  label={'Spouse: Current Passport or Travel Document Number:'}
                  onChange={(evt: any) => {
                    onChange(VQ.SPOUSE_PASSPORT_NUMBER, evt.currentTarget.value);
                  }}
                />
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
    </QuestionPage>
  );
};
export default VisaPrepFormFamilyPage1;
